import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tag,
  Space,
  Button,
  message,
  Descriptions,
  Image,
  Row,
  Col,
  Empty,
  Modal,
  Upload,
  Divider,
  InputNumber,
  Form,
} from 'antd';
import { EyeOutlined, CheckCircleOutlined, UploadOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const MisServicios = () => {
  const { user } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [finalizarModalVisible, setFinalizarModalVisible] = useState(false);
  const [formFinalizar] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [estadoPendiente, setEstadoPendiente] = useState('completado');
  const [ultimoIdServicio, setUltimoIdServicio] = useState(null);

  useEffect(() => {
    if (user && user.personal_id) {
      fetchServicios();
      
      // Auto-refresh cada 30 segundos para verificar nuevos servicios
      const interval = setInterval(() => {
        fetchServicios();
      }, 30000); // 30 segundos
      
      return () => clearInterval(interval);
    }
  }, [user]);

  // Efecto para cargar el kilometraje cuando se abre el modal de finalizar
  useEffect(() => {
    if (finalizarModalVisible && selectedServicio) {
      const cargarKilometraje = async () => {
        try {
          let kmActual = null;
          let unidadId = selectedServicio?.unidad_id;
          
          console.log('Cargando kilometraje para servicio:', selectedServicio.id);
          console.log('unidad_id del servicio:', unidadId);
          
          // Si el servicio no tiene unidad_id, intentar obtenerlo desde la asistencia del trabajador
          if (!unidadId && user?.personal_id) {
            try {
              const asistenciaResponse = await api.get(`/asistencias?personal_id=${user.personal_id}&unidad=1`);
              if (asistenciaResponse.data && asistenciaResponse.data.unidad_id) {
                unidadId = asistenciaResponse.data.unidad_id;
                console.log('Unidad obtenida desde asistencia:', unidadId);
              }
            } catch (error) {
              console.error('Error al obtener unidad desde asistencia:', error);
            }
          }
          
          // Obtener el kilometraje actual directamente de la unidad
          if (unidadId) {
            try {
              const unidadResponse = await api.get(`/unidades/${unidadId}`);
              console.log('Respuesta de unidad:', unidadResponse.data);
              if (unidadResponse.data && unidadResponse.data.km_actual !== null && unidadResponse.data.km_actual !== undefined) {
                kmActual = parseFloat(unidadResponse.data.km_actual);
                console.log('Kilometraje actual obtenido desde unidad:', kmActual);
              }
            } catch (error) {
              console.error('Error al obtener información de la unidad:', error);
            }
          } else {
            console.log('No se pudo obtener unidad_id del servicio ni de la asistencia');
          }
          
          console.log('Valor a establecer:', kmActual);
          
          if (kmActual !== null && kmActual !== undefined) {
            // Usar setTimeout para asegurar que el formulario esté completamente renderizado
            setTimeout(() => {
              formFinalizar.setFieldsValue({
                km_fin: kmActual
              });
              console.log('Valor establecido en el formulario:', kmActual);
            }, 200);
          }
        } catch (error) {
          console.error('Error al cargar kilometraje:', error);
        }
      };
      
      cargarKilometraje();
    } else if (!finalizarModalVisible) {
      // Resetear el formulario cuando se cierra el modal
      formFinalizar.resetFields();
    }
  }, [finalizarModalVisible, selectedServicio, user]);

  const fetchServicios = async () => {
    if (!user || !user.personal_id) {
      message.warning('No se encontró información del personal');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/servicios');
      // Filtrar solo los servicios asignados a este personal
      const misServicios = response.data.filter(
        s => s.trabajador_id && s.trabajador_id.toString() === user.personal_id.toString()
      );
      
      // Detectar si hay nuevos servicios
      if (ultimoIdServicio !== null && misServicios.length > 0) {
        const nuevoServicio = misServicios.find(s => s.id > ultimoIdServicio);
        if (nuevoServicio) {
          // Reproducir sonido y vibrar si hay nuevo servicio
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = 600;
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
          } catch (error) {
            console.error('Error al reproducir sonido:', error);
          }
          
          // Vibrar si está disponible
          if ('vibrate' in navigator) {
            try {
              navigator.vibrate([200, 100, 200]);
            } catch (error) {
              console.error('Error al vibrar:', error);
            }
          }
          
          message.info('¡Tienes un nuevo servicio asignado!');
        }
      }
      
      // Actualizar el último ID de servicio
      if (misServicios.length > 0) {
        const maxId = Math.max(...misServicios.map(s => s.id));
        setUltimoIdServicio(maxId);
      }
      
      setServicios(misServicios);
    } catch (error) {
      message.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = (record) => {
    setSelectedServicio(record);
    setFileList([]); // Limpiar lista de archivos al abrir el modal
    setModalVisible(true);
  };

  const handleDeleteImage = async (imageUrl, index) => {
    if (!selectedServicio || !selectedServicio.id) {
      message.error('No hay servicio seleccionado');
      return;
    }

    try {
      const imagenesActuales = selectedServicio.imagenes || [];
      const nuevasImagenes = imagenesActuales.filter((_, i) => i !== index);

      await api.put(`/servicios/${selectedServicio.id}`, {
        imagenes: nuevasImagenes
      });

      setSelectedServicio({
        ...selectedServicio,
        imagenes: nuevasImagenes
      });

      fetchServicios();
      message.success('Imagen eliminada exitosamente');
    } catch (error) {
      message.error('Error al eliminar imagen');
    }
  };

  const handleCambiarEstado = async (nuevoEstado, kmFin = null) => {
    if (!selectedServicio || !selectedServicio.id) {
      message.error('No hay servicio seleccionado');
      return;
    }

    try {
      const updateData = {
        estado: nuevoEstado
      };
      
      // Si se está finalizando o cancelando y hay km_fin, incluirlo
      if ((nuevoEstado === 'completado' || nuevoEstado === 'cancelado') && kmFin !== null) {
        updateData.km_fin = kmFin;
      }
      
      await api.put(`/servicios/${selectedServicio.id}`, updateData);

      setSelectedServicio({
        ...selectedServicio,
        estado: nuevoEstado,
        km_fin: kmFin !== null ? kmFin : selectedServicio.km_fin
      });

      fetchServicios();
      message.success('Estado del servicio actualizado exitosamente');
      setFinalizarModalVisible(false);
      formFinalizar.resetFields();
    } catch (error) {
      message.error('Error al actualizar el estado del servicio');
    }
  };

  const handleFinalizarServicio = () => {
    setFinalizarModalVisible(true);
  };

  const handleConfirmarFinalizar = async (values, estado = 'completado') => {
    await handleCambiarEstado(estado, values.km_fin);
  };

  const getEstadoColor = (estado) => {
    const colores = {
      pendiente: 'orange',
      en_espera: 'cyan',
      en_proceso: 'blue',
      completado: 'green',
      cancelado: 'red',
    };
    return colores[estado] || 'default';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      pendiente: 'PENDIENTE',
      en_espera: 'EN ESPERA',
      en_proceso: 'EN PROCESO',
      completado: 'COMPLETADO',
      cancelado: 'CANCELADO',
    };
    return labels[estado] || estado?.toUpperCase() || '-';
  };

  // Determinar qué botones mostrar según el estado actual
  const getBotonesEstadoDisponibles = (estadoActual) => {
    const botones = [];
    
    // Solo se puede cambiar a: en_proceso, completado, cancelado
    // No se puede cambiar desde completado o cancelado
    if (estadoActual === 'completado' || estadoActual === 'cancelado') {
      return botones; // No mostrar botones si ya está completado o cancelado
    }
    
    // Desde pendiente, en_espera o en_proceso se puede cambiar a:
    if (estadoActual !== 'en_proceso') {
      botones.push({ estado: 'en_proceso', label: 'En Proceso', color: 'blue' });
    }
    
    if (estadoActual !== 'completado') {
      botones.push({ estado: 'completado', label: 'Completar', color: 'green' });
    }
    
    if (estadoActual !== 'cancelado') {
      botones.push({ estado: 'cancelado', label: 'Cancelar', color: 'red' });
    }
    
    return botones;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) => {
        if (record.cliente_nombre) {
          return `${record.cliente_nombre} ${record.cliente_apellido || ''}`.trim();
        }
        return record.cliente_id || '-';
      },
    },
    {
      title: 'Lugar de Contacto',
      dataIndex: 'lugar_contacto',
      key: 'lugar_contacto',
      render: (text) => text || '-',
    },
    {
      title: 'Lugar de Destino',
      dataIndex: 'lugar_destino',
      key: 'lugar_destino',
      render: (text) => text || '-',
    },
    {
      title: 'Tipo de Servicio',
      dataIndex: 'tipo_servicio_nombre',
      key: 'tipo_servicio_nombre',
      render: (text) => text || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {getEstadoLabel(estado)}
        </Tag>
      ),
    },
    {
      title: 'Fecha Solicitud',
      dataIndex: 'fecha_solicitud',
      key: 'fecha_solicitud',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleVerDetalle(record)}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2>Mis Servicios Asignados</h2>
          <p>Servicios que te han sido asignados</p>
        </div>

        {servicios.length === 0 ? (
          <Empty description="No tienes servicios asignados" />
        ) : (
          <Table
            columns={columns}
            dataSource={servicios}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )}

        {selectedServicio && (
          <Modal
            title={`Detalle del Servicio #${selectedServicio.id}`}
            open={modalVisible}
            onCancel={() => {
              setModalVisible(false);
              setSelectedServicio(null);
            }}
            footer={[
              <Button 
                key="finalizar" 
                type="primary" 
                icon={<CheckOutlined />}
                onClick={handleFinalizarServicio}
                disabled={selectedServicio?.estado === 'completado'}
              >
                Finalizar Servicio
              </Button>,
              <Button key="close" onClick={() => {
                setModalVisible(false);
                setSelectedServicio(null);
              }}>
                Cerrar
              </Button>
            ]}
            width={window.innerWidth < 768 ? '95%' : 900}
            style={{ top: window.innerWidth < 768 ? 10 : 20 }}
            bodyStyle={{ maxHeight: window.innerWidth < 768 ? '80vh' : 'auto', overflowY: 'auto' }}
          >
            <Descriptions bordered column={window.innerWidth < 768 ? 1 : 2}>
              <Descriptions.Item label="Cliente">
                {selectedServicio.cliente_nombre 
                  ? `${selectedServicio.cliente_nombre} ${selectedServicio.cliente_apellido || ''}`.trim()
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Tag color={getEstadoColor(selectedServicio.estado)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {getEstadoLabel(selectedServicio.estado)}
                  </Tag>
                  {getBotonesEstadoDisponibles(selectedServicio.estado).length > 0 && (
                    <Space wrap>
                      {getBotonesEstadoDisponibles(selectedServicio.estado).map((boton) => (
                        <Button
                          key={boton.estado}
                          type={boton.estado === 'completado' ? 'primary' : 'default'}
                          danger={boton.estado === 'cancelado'}
                          onClick={() => {
                            if (boton.estado === 'completado' || boton.estado === 'cancelado') {
                              // Guardar el estado que se va a cambiar para usarlo en el modal
                              setEstadoPendiente(boton.estado);
                              handleFinalizarServicio();
                            } else {
                              handleCambiarEstado(boton.estado);
                            }
                          }}
                          style={{ marginTop: 8 }}
                        >
                          {boton.label}
                        </Button>
                      ))}
                    </Space>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Lugar de Contacto" span={2}>
                {selectedServicio.lugar_contacto || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Lugar de Destino" span={2}>
                {selectedServicio.lugar_destino || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Placa">
                {selectedServicio.placa || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Vehículo">
                {selectedServicio.tipo_vehiculo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Marca">
                {selectedServicio.marca_vehiculo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Modelo">
                {selectedServicio.modelo_vehiculo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Año">
                {selectedServicio.anio_vehiculo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Servicio">
                {selectedServicio.tipo_servicio_nombre || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Aseguradora">
                {selectedServicio.aseguradora_nombre || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Km. Inicio">
                {selectedServicio.km_inicio || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Km. Fin">
                {selectedServicio.km_fin || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Costo">
                {selectedServicio.costo ? `S/ ${parseFloat(selectedServicio.costo).toFixed(2)}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Solicitud">
                {selectedServicio.fecha_solicitud 
                  ? dayjs(selectedServicio.fecha_solicitud).format('DD/MM/YYYY HH:mm') 
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Observaciones" span={2}>
                {selectedServicio.observaciones || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Imágenes del Servicio</Divider>
            
            <div style={{ marginBottom: 16 }}>
              <Upload.Dragger
                name="imagenes"
                multiple
                fileList={fileList}
                accept="image/*"
                disabled={selectedServicio?.estado === 'completado' || uploadingImages}
                onChange={({ fileList: newFileList }) => {
                  setFileList(newFileList);
                }}
                customRequest={async ({ file, onSuccess, onError, onProgress }) => {
                  if (!selectedServicio || !selectedServicio.id) {
                    onError(new Error('No hay servicio seleccionado'));
                    message.error('No hay servicio seleccionado');
                    return;
                  }

                  // Simular progreso inicial
                  onProgress({ percent: 0 });
                  
                  setUploadingImages(true);
                  try {
                    const formData = new FormData();
                    formData.append('imagenes[]', file);
                    formData.append('tipo', 'servicios');

                    onProgress({ percent: 30 });

                    const response = await api.post('/servicios/upload-images', formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });

                    onProgress({ percent: 70 });

                    if (response.data && response.data.urls && response.data.urls.length > 0) {
                      const imagenesActuales = selectedServicio.imagenes || [];
                      const nuevasImagenes = [...imagenesActuales, ...response.data.urls];

                      await api.put(`/servicios/${selectedServicio.id}`, {
                        imagenes: nuevasImagenes
                      });

                      onProgress({ percent: 100 });

                      // Actualizar el servicio seleccionado con las nuevas imágenes
                      setSelectedServicio({
                        ...selectedServicio,
                        imagenes: nuevasImagenes
                      });

                      // Refrescar la lista de servicios
                      await fetchServicios();
                      
                      // Limpiar la lista de archivos después de subir
                      setTimeout(() => {
                        setFileList([]);
                      }, 500);
                      
                      onSuccess('ok');
                      message.success('Imagen agregada exitosamente');
                    } else {
                      const errorMsg = response.data?.message || 'No se recibieron URLs de imágenes';
                      onError(new Error(errorMsg));
                      message.error(errorMsg);
                    }
                  } catch (error) {
                    console.error('Error al subir imagen:', error);
                    const errorMsg = error.response?.data?.message || error.message || 'Error al subir imagen';
                    onError(error);
                    message.error(errorMsg);
                  } finally {
                    setUploadingImages(false);
                  }
                }}
                onRemove={(file) => {
                  const newFileList = fileList.filter(item => item.uid !== file.uid);
                  setFileList(newFileList);
                  return true;
                }}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: '48px', color: selectedServicio?.estado === 'completado' ? '#d9d9d9' : '#1890ff' }} />
                </p>
                <p className="ant-upload-text">
                  {selectedServicio?.estado === 'completado' 
                    ? 'No se pueden agregar imágenes a servicios completados' 
                    : 'Haz clic o arrastra imágenes aquí para agregar'}
                </p>
                {selectedServicio?.estado !== 'completado' && (
                  <p className="ant-upload-hint">
                    Soporta: JPG, PNG, GIF (máx. 10MB por imagen)
                  </p>
                )}
              </Upload.Dragger>
            </div>

            {selectedServicio.imagenes && Array.isArray(selectedServicio.imagenes) && selectedServicio.imagenes.length > 0 ? (
              <Row gutter={[8, 8]}>
                {selectedServicio.imagenes.map((url, index) => (
                  <Col key={index} span={8}>
                    <div style={{ position: 'relative' }}>
                      <Image
                        src={getImageUrl(url)}
                        alt={`Imagen ${index + 1}`}
                        style={{ width: '100%', borderRadius: 4 }}
                        preview
                      />
                      <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                        onClick={() => handleDeleteImage(url, index)}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description="No hay imágenes agregadas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Modal>
        )}

        {/* Modal para finalizar/cancelar servicio con kilometraje */}
        <Modal
          title={estadoPendiente === 'cancelado' ? 'Cancelar Servicio' : 'Finalizar Servicio'}
          open={finalizarModalVisible}
          onCancel={() => {
            setFinalizarModalVisible(false);
            formFinalizar.resetFields();
            setEstadoPendiente('completado');
          }}
          footer={null}
          width={500}
        >
          <Form
            form={formFinalizar}
            layout="vertical"
            onFinish={(values) => handleConfirmarFinalizar(values, estadoPendiente)}
            initialValues={{
              km_fin: null
            }}
          >
            <Form.Item
              name="km_fin"
              label="Kilometraje Final de la Unidad"
              rules={[
                { required: true, message: 'Ingresa el kilometraje final' },
                { type: 'number', min: 0, message: 'El kilometraje debe ser mayor o igual a 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Ingresa el kilometraje final"
                min={0}
                precision={2}
                formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  danger={estadoPendiente === 'cancelado'}
                >
                  {estadoPendiente === 'cancelado' ? 'Cancelar Servicio' : 'Finalizar Servicio'}
                </Button>
                <Button onClick={() => {
                  setFinalizarModalVisible(false);
                  formFinalizar.resetFields();
                  setEstadoPendiente('completado');
                }}>
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default MisServicios;

